import { config } from "JS/Config";
import { messaging, getErrorMessage } from "JS/Helpers/UserMessaging";
import {
    Content,
    ContentWithMedia,
    EventActions,
    EventCategories,
    EventNames,
    GiftWithMedia,
    Playlist,
    PlaylistAudio,
    ReceivedGift,
} from "JS/Models";
import {
    ResumePlaylist,
    migratePlaylistData,
    setGlobalPlaylist,
    updatePlaylistResume,
} from "JS/Redux/Playlist";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import { PlaylistService } from "JS/Services/Playlist";
import { useSnackbar } from "JS/Helpers/Snackbar";
import React, { useMemo } from "react";
import { useCallback, useEffect, useState } from "react";
import { useGlobalAudios } from "./Audio";
import { useFirebaseLogger } from "./Firebase";

const service = new PlaylistService();

export const usePlaylists = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<{ error: string }>();
    const [isEmpty, setEmpty] = useState<{ empty: boolean }>();
    const dispatch = useAppDispatch();

    const { userPlaylists, playlistResumeMap, allPlaylists } =
        useGlobalPlaylists();

    const fetch = useCallback(() => {
        setLoading(userPlaylists.length === 0);
        service
            .getPlaylists()
            .then((response) => {
                const playlists = response.data;
                setEmpty({ empty: playlists.length === 0 });
                const toSave = playlists.map((p) => {
                    const existing = userPlaylists?.find((e) => e.id === p.id);
                    return {
                        ...p,
                        audios: existing?.audios || [],
                        count: existing?.count || "",
                        showCount: existing?.showCount || false,
                    };
                });
                dispatch(
                    setGlobalPlaylist({
                        playlistsMap: {
                            ...allPlaylists,
                            [config?.user?.memberId]: toSave,
                        },
                        playlistResumeMap,
                    }),
                );
            })
            .catch((e) => {
                onError(e.toString());
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!skip) fetch();
    }, [skip]);

    function onError(message: string) {
        if (userPlaylists?.length === 0) setError({ error: message });
    }
    return {
        loading,
        error,
        isEmpty,
        fetch,
    };
};

export const useCreatePlaylist = () => {
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { logFirebaseEvent } = useFirebaseLogger();
    const dispatch = useAppDispatch();
    const { userPlaylists, playlistResumeMap, allPlaylists } =
        useGlobalPlaylists();

    const create = (title: string, onSuccess?: (id: number) => void) => {
        setLoading(true);
        service
            .createPlaylist(title)
            .then((r) => {
                const toSave = userPlaylists?.map((p) => p);
                toSave.push({
                    id: r.data,
                    name: title,
                    sortOrder: 0,
                    count: "0",
                    showCount: false,
                });
                dispatch(
                    setGlobalPlaylist({
                        playlistsMap: {
                            ...allPlaylists,
                            [config?.user?.memberId]: toSave,
                        },
                        playlistResumeMap,
                    }),
                );
                logFirebaseEvent(EventNames.AUDIO_PLAYLIST_ADDED, {
                    action: EventActions.ADDED,
                    category: EventCategories.AUDIO_PLAYLIST,
                    playlistId: r.data.toString(),
                });
                enqueueSnackbar(messaging.playlist.created, {
                    variant: "success",
                });
                onSuccess && onSuccess(r.data);
            })
            .catch((e) =>
                enqueueSnackbar(e?.response?.data?.message?.toString(), {
                    variant: "error",
                }),
            )
            .finally(() => setLoading(false));
    };

    return {
        loading,
        create,
    };
};

export const useDeletePlaylist = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<{ error: string }>();
    const [success, setSuccess] = useState<{ success: string }>();
    const dispatch = useAppDispatch();
    const { userPlaylists, playlistResumeMap, allPlaylists } =
        useGlobalPlaylists();

    const deletePlaylist = (id: number) => {
        setLoading(true);
        service
            .deletePlaylist(id)
            .then((r) => {
                setSuccess({ success: "Playlist deleted successfully" });
                const toSave = userPlaylists
                    .map((p) => p)
                    .filter((p) => p.id !== +id);
                dispatch(
                    setGlobalPlaylist({
                        playlistsMap: {
                            ...allPlaylists,
                            [config?.user?.memberId]: toSave,
                        },
                        playlistResumeMap,
                    }),
                );
            })
            .catch((e) => setError({ error: e.toString() }))
            .finally(() => setLoading(false));
    };

    return {
        loading,
        error,
        success,
        deletePlaylist,
    };
};

export const useRenamePlaylist = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<{ error: string }>();
    const [success, setSuccess] = useState<{ success: string }>();
    const dispatch = useAppDispatch();
    const { userPlaylists, playlistResumeMap, allPlaylists } =
        useGlobalPlaylists();

    const rename = (title: string, id: number) => {
        setLoading(true);
        service
            .renamePlaylist(id, title)
            .then((response) => {
                setSuccess({ success: "Playlist renamed successfully" });
                const toSave = userPlaylists?.map((p) =>
                    p.id !== +id
                        ? p
                        : {
                              ...p,
                              name: title,
                          },
                );
                dispatch(
                    setGlobalPlaylist({
                        playlistsMap: {
                            ...allPlaylists,
                            [config?.user?.memberId]: toSave,
                        },
                        playlistResumeMap,
                    }),
                );
            })
            .catch((e) => setError({ error: getErrorMessage(e) }))
            .finally(() => setLoading(false));
    };

    return {
        loading,
        error,
        success,
        rename,
    };
};

export const useSortPlaylists = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<{ error: string }>();
    const [success, setSuccess] = useState<{ success: string }>();
    const dispatch = useAppDispatch();
    const { playlistResumeMap, allPlaylists } = useGlobalPlaylists();

    const sort = (playlists: Playlist[]) => {
        setLoading(true);
        service
            .sortPlaylist(playlists.map((p) => p.id.toString()))
            .then((response) => {
                setSuccess({
                    success: "Playlists position updated successfully",
                });
                const toSave: Playlist[] = [];
                playlists.forEach((p, i) => {
                    toSave.push({
                        ...p,
                        sortOrder: i,
                    });
                });
                dispatch(
                    setGlobalPlaylist({
                        playlistsMap: {
                            ...allPlaylists,
                            [config?.user?.memberId]: toSave,
                        },
                        playlistResumeMap,
                    }),
                );
            })
            .catch((e) => {
                setError({ error: e.toString() });
            })
            .finally(() => setLoading(false));
    };

    return {
        loading,
        error,
        success,
        sort,
    };
};

export const usePlaylistAudios = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const { userPlaylists, playlistResumeMap, allPlaylists } =
        useGlobalPlaylists();
    const dispatch = useAppDispatch();
    const { setPlaylistAudioDetail } = useGlobalAudios();

    const getAudios = (id: string) => {
        const audiosInStore = userPlaylists.find((p) => p.id === +id)?.audios;
        setLoading(audiosInStore && audiosInStore.length > 0);
        service
            .getPlaylistAudios(id)
            .then((r) => {
                const responseAudios = r.data;
                const playlistsToSave = userPlaylists.map((p) =>
                    p.id === +id
                        ? {
                              ...p,
                              count: responseAudios.length.toString(),
                              showCount: responseAudios.length > 0,
                              audios: responseAudios.map((a) => {
                                  return {
                                      ...a,
                                      media: null,
                                      description: null,
                                  };
                              }),
                          }
                        : p,
                );

                dispatch(
                    setGlobalPlaylist({
                        playlistsMap: {
                            ...allPlaylists,
                            [config?.user?.memberId]: playlistsToSave,
                        },
                        playlistResumeMap,
                    }),
                );
                setPlaylistAudioDetail(responseAudios);
                if (responseAudios.length === 0)
                    setError("There are no audios in this playlist.");
            })
            .catch((e) => {
                setError(e.toString());
            })
            .finally(() => setLoading(false));
    };

    return {
        loading,
        error,
        getAudios,
    };
};

export const useRemoveAudio = () => {
    const [loading, setLoading] = useState(false);
    const { userPlaylists, playlistResumeMap, allPlaylists } =
        useGlobalPlaylists();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { logFirebaseEvent } = useFirebaseLogger();

    const removeAudio = (
        playlistId: string,
        audio: Content | ReceivedGift,
        isGift: boolean,
    ) => {
        setLoading(true);
        return service
            .removeAudioFromPlaylist(playlistId, audio.nid)
            .then(() => {
                logFirebaseEvent(
                    isGift
                        ? EventNames.GIFT_AUDIO_DELETED_FROM_PLAYLIST
                        : EventNames.AUDIO_DELETED_FROM_PLAYLIST,
                    {
                        action: EventActions.DELETED_FROM_PLAYLIST,
                        category: isGift
                            ? EventCategories.GIFT_AUDIOS
                            : EventCategories.AUDIOS,
                        skuId: audio.sku_id,
                        nId: audio.nid,
                        playlistId: playlistId,
                        contentTitle: audio.title,
                    },
                );
                const toSave = userPlaylists?.map((p) => {
                    if (p.id === +playlistId) {
                        const audios = p.audios.filter(
                            (a) => a.nid !== audio.nid,
                        );
                        return {
                            ...p,
                            showCount: audios.length > 0,
                            count: audios.length.toString(),
                            audios: audios,
                        };
                    } else return p;
                });

                dispatch(
                    setGlobalPlaylist({
                        playlistsMap: {
                            ...allPlaylists,
                            [config?.user?.memberId]: toSave,
                        },
                        playlistResumeMap,
                    }),
                );
                enqueueSnackbar("Audio removed from playlist.", {
                    variant: "success",
                });
            })
            .catch((e) => {
                enqueueSnackbar(e.message, {
                    variant: "error",
                });
            })
            .finally(() => setLoading(false));
    };

    return {
        loading,
        removeAudio,
    };
};

export const useAddToPlaylist = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<{ error: string }>();
    const [success, setSuccess] = useState<{
        playlistId: string;
        success: string;
    }>();
    const { userPlaylists, playlistResumeMap, allPlaylists } =
        useGlobalPlaylists();
    const dispatch = useAppDispatch();

    const addAudio = (
        playlistId: string,
        audioToAdd: ContentWithMedia | GiftWithMedia,
        contentType: string,
    ) => {
        setLoading(true);
        service
            .addAudioToPlaylist(playlistId, audioToAdd.nid, contentType)
            .then(() => {
                setSuccess({
                    playlistId: playlistId,
                    success: messaging.playlist.audioAdded,
                });
                const toSave = userPlaylists?.map((p) => {
                    if (p.id === +playlistId) {
                        const newAudios: PlaylistAudio[] = p.audios
                            ? [...p.audios]
                            : [];
                        const audio: PlaylistAudio = {
                            media: audioToAdd.media,
                            description: audioToAdd.description,
                            nid: audioToAdd.nid,
                            sortOrder: newAudios.length.toString(),
                        };
                        newAudios.push(audio);
                        return {
                            ...p,
                            showCount: p.showCount ? p.showCount : false,
                            count: newAudios.length.toString(),
                            audios: newAudios,
                        };
                    } else return p;
                });
                dispatch(
                    setGlobalPlaylist({
                        playlistsMap: {
                            ...allPlaylists,
                            [config?.user?.memberId]: toSave,
                        },
                        playlistResumeMap,
                    }),
                );
            })
            .catch((e) =>
                setError({
                    error:
                        e?.response?.status === 409
                            ? messaging.playlist.audioAlreadyExists
                            : getErrorMessage(e),
                }),
            )
            .finally(() => setLoading(false));
    };

    return {
        loading,
        error,
        success,
        addAudio,
    };
};

export const useSortAudios = () => {
    const [loading, setLoading] = useState(false);
    const { userPlaylists, playlistResumeMap, allPlaylists } =
        useGlobalPlaylists();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const sortAudios = (playlistId: string, audios: { nid: string }[]) => {
        setLoading(true);
        service
            .sortAudiosInPlaylist(
                playlistId,
                audios.map((a) => a.nid),
            )
            .then(() => {
                const toSave = userPlaylists?.map((p) => {
                    if (p.id === +playlistId) {
                        let newAudios: PlaylistAudio[] = [];
                        audios.forEach((a, i) =>
                            newAudios.push({
                                ...p.audios.find((pa) => pa.nid === a.nid),
                                sortOrder: i.toString(),
                            }),
                        );
                        return {
                            ...p,
                            audios: newAudios,
                        };
                    } else return p;
                });
                dispatch(
                    setGlobalPlaylist({
                        playlistsMap: {
                            ...allPlaylists,
                            [config?.user?.memberId]: toSave,
                        },
                        playlistResumeMap,
                    }),
                );
                enqueueSnackbar(
                    "Playlist items position updated successfully.",
                    {
                        variant: "success",
                    },
                );
            })
            .catch((e) =>
                enqueueSnackbar(e.message, {
                    variant: "error",
                }),
            )
            .finally(() => setLoading(false));
    };

    return {
        loading,
        sortAudios,
    };
};

export const useGlobalPlaylists = () => {
    const dispatch = useAppDispatch();

    const allData = useAppSelector((state) => state.playlist);

    const resumeMap = useMemo(
        () =>
            allData.playlistResumeMap ? { ...allData.playlistResumeMap } : {},
        [allData],
    );
    const resumeList = useMemo(
        () =>
            resumeMap[config.user.memberId]
                ? [...resumeMap[config.user.memberId]]
                : [],
        [resumeMap],
    );

    const setGlobalPlaylists = (playlists: Playlist[]) => {
        dispatch(
            setGlobalPlaylist({
                ...allData,
                playlistsMap: {
                    ...allData.playlistsMap,
                    [config?.user?.memberId]: playlists,
                },
            }),
        );
    };

    const userPlaylists = useMemo(
        () =>
            allData?.playlistsMap &&
            allData?.playlistsMap[`${config?.user?.memberId}`]
                ? allData?.playlistsMap[`${config?.user?.memberId}`]
                : [],
        [allData],
    );

    const allPlaylists = useMemo(
        () => (allData?.playlistsMap ? allData?.playlistsMap : {}),
        [allData],
    );

    return {
        userPlaylists,
        allPlaylists,
        playlistResumeMap: resumeMap,
        userPlaylistResume: resumeList,
        setGlobalPlaylists,
        savePlaylistResumeState: (state: ResumePlaylist) => {
            const existing = resumeList.find((r) => r.id === state.id);
            const newResumeList = resumeList;
            if (existing)
                newResumeList[newResumeList.indexOf(existing)] = state;
            else newResumeList.push(state);
            const newResumeMap = resumeMap;
            newResumeMap[config.user.memberId] = newResumeList;
            dispatch(
                setGlobalPlaylist({
                    ...allData,
                    playlistResumeMap: newResumeMap,
                }),
            );
        },
        playlistResumeState: (
            playlist: Playlist,
            audios?: (Content | ReceivedGift)[],
        ) => {
            const resumeData = resumeList.find((r) => +r.id === playlist.id);
            if (resumeData) {
                const nids = !!audios
                    ? audios.map((a) => a.nid)
                    : playlist.audios.map((a) => a.nid);
                const existingNid = nids.find(
                    (nid) => nid === resumeData.resumeAudioNid,
                );
                return {
                    resumeAudioIndex: !!existingNid
                        ? nids.indexOf(existingNid)
                        : "0",
                    resumeBundleIndex: !!existingNid
                        ? resumeData.resumeBundleIndex
                        : "0",
                };
            }
            return undefined;
        },
        migratePlaylistData: () => dispatch(migratePlaylistData()),
        syncPlaylistData: (state: ResumePlaylist[]) => {
            dispatch(
                updatePlaylistResume({
                    ...resumeMap,
                    [config.user.memberId]: state,
                }),
            );
        },
    };
};

export const usePlaylistCommonHandlers = (
    audio: GiftWithMedia | ContentWithMedia,
) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isCreatePlaylist, setCreatePlaylist] = useState(false);

    const { userPlaylists: playlistsInStore } = useGlobalPlaylists();
    const {
        loading: addToPlaylistLoading,
        error: addToPlaylistError,
        success: addToPlaylistSuccess,
        addAudio,
    } = useAddToPlaylist();
    const {
        loading: playlistsLoading,
        error: playlistsError,
        isEmpty: playlistsEmpty,
        fetch: fetchPlaylists,
    } = usePlaylists(true);
    const { loading: createLoading, create: createPlaylist } =
        useCreatePlaylist();

    const [anchorPlaylist, setAnchorPlaylist] =
        React.useState<null | HTMLElement>(null);
    const openPlaylistSelection = Boolean(anchorPlaylist);

    useEffect(() => {
        if (
            openPlaylistSelection &&
            (playlistsInStore.length === 0 ||
                //Remove below check after few months. It is used to migrate old playlist structure (playlist_id) to new structure (id)
                playlistsInStore[0]?.id === undefined)
        )
            fetchPlaylists();
    }, [openPlaylistSelection, playlistsInStore, fetchPlaylists]);

    useEffect(() => {
        if (playlistsError) {
            enqueueSnackbar(playlistsError.error, {
                variant: "error",
            });
        }
    }, [playlistsError, enqueueSnackbar]);

    useEffect(() => {
        if (addToPlaylistError) {
            enqueueSnackbar(addToPlaylistError.error, {
                variant: "error",
            });
        }
    }, [addToPlaylistError, enqueueSnackbar]);

    useEffect(() => {
        const shouldCreate = playlistsEmpty && playlistsEmpty.empty;
        setCreatePlaylist(shouldCreate);
        if (shouldCreate) setAnchorPlaylist(null);
    }, [playlistsEmpty, setCreatePlaylist]);

    const handlePlaylistClick = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        setAnchorPlaylist(e.currentTarget);
    };

    const createClick = (input: string) => {
        setCreatePlaylist(false);
        if (input && input.length <= 45)
            createPlaylist(input, (id) =>
                addAudio(
                    id.toString(),
                    audio,
                    "content_item_type_name" in audio
                        ? audio.content_item_type_name
                        : "MP3",
                ),
            );
        else
            enqueueSnackbar(
                !input || input.length === 0
                    ? messaging?.playlist?.emptyInput
                    : messaging?.playlist?.characterInput,
                {
                    variant: "warning",
                },
            );
    };

    return {
        isCreatePlaylist,
        addToPlaylistLoading,
        addToPlaylistSuccess,
        playlistsLoading,
        createLoading,
        openPlaylistSelection,
        playlistsInStore,
        handlePlaylistClick,
        createClick,
        addAudio,
        setAnchorPlaylist,
        setCreatePlaylist,
    };
};

export const usePlaylistResume = (
    id: string,
    audioNid: string,
    bundleIndex?: number,
) => {
    const { savePlaylistResumeState } = useGlobalPlaylists();

    useEffect(() => {
        if (id) {
            savePlaylistResumeState({
                id,
                resumeAudioNid: audioNid,
                resumeBundleIndex: bundleIndex,
            });
        }
    }, [id, audioNid, bundleIndex]);

    return {
        savePlaylistResumeState,
    };
};

export const useCreatePlaylistWithContents = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const { userPlaylists, playlistResumeMap, allPlaylists } =
        useGlobalPlaylists();

    const createPlaylistWithContents = async (
        title: string,
        content: (Content | ReceivedGift)[],
    ) => {
        setLoading(true);
        return service
            .createPlaylistWithContents(title, content)
            .then((r) => {
                const newId = r.data;
                const audios: PlaylistAudio[] = (content ?? []).map((c, i) => ({
                    media: null,
                    description: null,
                    nid: c.nid,
                    sortOrder: i.toString(),
                }));
                const newPlaylist: Playlist = {
                    id: newId,
                    name: title,
                    sortOrder: 0,
                    count: audios.length.toString(),
                    showCount: audios.length > 0,
                    audios,
                };
                const save = (list: Playlist[]) => {
                    dispatch(
                        setGlobalPlaylist({
                            playlistsMap: {
                                ...allPlaylists,
                                [config?.user?.memberId]: list
                                    .filter((p) => p.id !== newId)
                                    .concat(newPlaylist),
                            },
                            playlistResumeMap,
                        }),
                    );
                    enqueueSnackbar(messaging.playlist.created, {
                        variant: "success",
                    });
                };
                // If the list is already loaded, just add the new playlist (no extra call); otherwise fetch the list first, then add it on top so it shows without a manual reload
                if (userPlaylists.length > 0 && userPlaylists[0]?.id !== undefined) {
                    save(userPlaylists.map((p) => p));
                    return;
                }
                return service
                    .getPlaylists()
                    .then((res) =>
                        res.data.map((p) => {
                            const existing = userPlaylists?.find(
                                (e) => e.id === p.id,
                            );
                            return {
                                ...p,
                                audios: existing?.audios || [],
                                count: existing?.count || "",
                                showCount: existing?.showCount || false,
                            };
                        }),
                    )
                    .catch(() => userPlaylists.map((p) => p))
                    .then(save);
            })
            .catch((e) =>
                enqueueSnackbar(e?.response?.data?.message, {
                    variant: "error",
                }),
            )
            .finally(() => setLoading(false));
    };

    return {
        loading,
        createPlaylistWithContents,
    };
};
